import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  FileText, Upload, Trash2, Download, Eye, X, File,
  Image as ImageIcon, Search, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const getFileIcon = (contentType) => {
  if (contentType?.startsWith('image/')) return <ImageIcon size={20} className="text-blue-500" />;
  return <FileText size={20} className="text-red-500" />;
};

const MyDocuments = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const fileInputRef = useRef(null);

  const isProvider = user?.role === 'provider';

  const fetchDocuments = React.useCallback(async (patientId) => {
    const pid = patientId || user?.id;
    try {
      const response = await axios.get(`${API}/documents/${pid}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDocuments(response.data);
    } catch (error) {
      // console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchPatients = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API}/patients`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPatients(response.data);
      if (response.data.length > 0) {
        setSelectedPatient(response.data[0].user_id);
      }
    } catch (error) {
      // console.error('Error fetching patients:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isProvider) {
      fetchPatients();
    } else {
      fetchDocuments();
    }
  }, [isProvider, fetchPatients, fetchDocuments]);

  useEffect(() => {
    if (isProvider && selectedPatient) {
      fetchDocuments(selectedPatient);
    }
  }, [isProvider, selectedPatient, fetchDocuments]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'jpg', 'jpeg', 'png'].includes(ext)) {
      toast.error('Only PDF, JPG, JPEG, and PNG files are accepted');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    const pid = isProvider ? selectedPatient : 'self';
    try {
      await axios.post(`${API}/documents/upload?patient_id=${pid}`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Document uploaded successfully');
      fetchDocuments(isProvider ? selectedPatient : undefined);
    } catch (error) {
      // console.error('Error uploading:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId, filename) => {
    if (!window.confirm(`Delete "${filename}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Document deleted');
      fetchDocuments(isProvider ? selectedPatient : undefined);
    } catch (error) {
      // console.error('Error deleting:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handlePreview = (doc) => {
    setPreviewDoc(doc);
  };

  const handleDownload = (doc) => {
    const token = localStorage.getItem('token');
    const url = `${API}/documents/${doc.id}/view?auth=${token}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.original_filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const getPreviewUrl = (doc) => {
    const token = localStorage.getItem('token');
    return `${API}/documents/${doc.id}/view?auth=${token}`;
  };

  const getPatientName = (p) => {
    const id = p.identifying_data || {};
    return [id.first_name, id.middle_name, id.last_name].filter(Boolean).join(' ') || 'Unknown';
  };

  const filteredDocs = documents.filter(doc =>
    doc.original_filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-700"></div>
          <p className="mt-4 text-slate-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="my-documents-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">My Documents</h1>
          <p className="text-slate-600">Upload, view, and manage health documents (PDFs and images).</p>
        </div>

        {/* Provider: Patient Selector */}
        {isProvider && (
          <div className="mb-6 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <label className="text-sm font-semibold text-slate-700 mb-2 block">Select Patient</label>
            <select
              value={selectedPatient}
              onChange={(e) => setSelectedPatient(e.target.value)}
              className="w-full rounded-lg border-slate-200 px-4 py-2.5"
              data-testid="patient-selector"
            >
              {patients.map(p => (
                <option key={p.user_id} value={p.user_id}>{getPatientName(p)}</option>
              ))}
            </select>
          </div>
        )}

        {/* Upload + Search Bar */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by file name..."
                className="pl-10"
                data-testid="document-search"
              />
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleUpload}
                className="hidden"
                data-testid="file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || (isProvider && !selectedPatient)}
                className="bg-indigo-950 hover:bg-indigo-900 text-white w-full sm:w-auto min-h-[44px] px-5"
                data-testid="upload-button"
              >
                <Upload size={18} className="mr-2" />
                {uploading ? 'Uploading...' : 'Upload File'}
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">Accepted: PDF, JPG, JPEG, PNG. Max 10MB per file.</p>
        </div>

        {/* Documents List */}
        {filteredDocs.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center shadow-sm">
            <File size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 font-medium mb-1">
              {searchTerm ? 'No documents match your search' : 'No documents yet'}
            </p>
            <p className="text-slate-400 text-sm">
              {searchTerm ? 'Try a different search term.' : 'Upload your health documents using the button above.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredDocs.map((doc, index) => (
              <div
                key={doc.id}
                className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:border-slate-300 transition-colors"
                data-testid={`document-row-${index}`}
              >
                <div className="flex items-center gap-4">
                  {/* File Icon */}
                  <div className="hidden sm:flex w-12 h-12 rounded-lg bg-slate-100 items-center justify-center shrink-0">
                    {getFileIcon(doc.content_type)}
                  </div>

                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate" data-testid={`document-name-${index}`}>
                      {doc.original_filename}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="text-xs text-slate-500">{formatFileSize(doc.size)}</span>
                      <span className="text-xs text-slate-300">|</span>
                      <span className="text-xs text-slate-500">{formatDate(doc.created_at)}</span>
                      {doc.uploaded_by_role === 'provider' && (
                        <>
                          <span className="text-xs text-slate-300">|</span>
                          <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
                            Uploaded by {doc.uploaded_by_name}
                          </Badge>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      onClick={() => handlePreview(doc)}
                      variant="outline"
                      size="sm"
                      className="border-slate-200 min-h-[40px] min-w-[40px] px-2 sm:px-3"
                      data-testid={`preview-btn-${index}`}
                    >
                      <Eye size={16} />
                      <span className="hidden sm:inline ml-1">Preview</span>
                    </Button>
                    <Button
                      onClick={() => handleDownload(doc)}
                      variant="outline"
                      size="sm"
                      className="border-slate-200 min-h-[40px] min-w-[40px] px-2 sm:px-3"
                      data-testid={`download-btn-${index}`}
                    >
                      <Download size={16} />
                      <span className="hidden sm:inline ml-1">Download</span>
                    </Button>
                    {user?.role === 'patient' && (
                      <Button
                        onClick={() => handleDelete(doc.id, doc.original_filename)}
                        variant="outline"
                        size="sm"
                        className="border-red-200 text-red-600 hover:bg-red-50 min-h-[40px] min-w-[40px] px-2 sm:px-3"
                        data-testid={`delete-btn-${index}`}
                      >
                        <Trash2 size={16} />
                        <span className="hidden sm:inline ml-1">Delete</span>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        {documents.length > 0 && (
          <div className="mt-4 text-sm text-slate-500 text-right">
            {filteredDocs.length} of {documents.length} documents
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="preview-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="flex items-center gap-3 min-w-0">
                {getFileIcon(previewDoc.content_type)}
                <span className="font-medium text-slate-900 truncate">{previewDoc.original_filename}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  onClick={() => handleDownload(previewDoc)}
                  variant="outline"
                  size="sm"
                  className="border-slate-200"
                  data-testid="modal-download-btn"
                >
                  <Download size={16} className="mr-1" />
                  Download
                </Button>
                <Button
                  onClick={() => setPreviewDoc(null)}
                  variant="outline"
                  size="sm"
                  className="border-slate-200"
                  data-testid="modal-close-btn"
                >
                  <X size={16} />
                </Button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-4 bg-slate-50 flex items-center justify-center">
              {previewDoc.content_type?.startsWith('image/') ? (
                <img
                  src={getPreviewUrl(previewDoc)}
                  alt={previewDoc.original_filename}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg shadow"
                  data-testid="preview-image"
                />
              ) : (
                <iframe
                  src={getPreviewUrl(previewDoc)}
                  title={previewDoc.original_filename}
                  className="w-full h-[70vh] rounded-lg border border-slate-200"
                  data-testid="preview-pdf"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyDocuments;
